import React from 'react';
import { Helmet } from 'react-helmet';

const SEOHead = ({ 
  title, 
  description, 
  keywords, 
  canonicalUrl, 
  ogImage, 
  ogType = 'website',
  structuredData,
  noIndex = false,
  pageType = 'website'
}) => {
  const defaultTitle = 'BloodHero - Blood Donation Platform | Connect Donors & Save Lives';
  const defaultDescription = 'BloodHero is a comprehensive blood donation platform connecting donors with those in need. Emergency blood requests, NGO projects, vehicle booking, and 24/7 support. Save lives today!';
  const defaultKeywords = 'blood donation, blood donor, emergency blood, blood bank, NGO blood donation, blood donation platform, save lives, blood donation India';
  const defaultImage = '/images/blood-hero-og-image.jpg';
  const baseUrl = 'https://www.thebloodhero.com';

  const finalTitle = title ? `${title} | BloodHero` : defaultTitle;
  const finalDescription = description || defaultDescription;
  const finalKeywords = keywords || defaultKeywords;
  const finalImage = ogImage || defaultImage;
  const finalUrl = canonicalUrl ? `${baseUrl}${canonicalUrl}` : baseUrl;

  // Default structured data for organization
  const defaultStructuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "BloodHero",
    "url": baseUrl,
    "logo": `${baseUrl}/images/logo1.png`,
    "description": "Blood donation platform connecting donors with those in need",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "IN",
      "addressRegion": "Haryana",
      "addressLocality": "Yamuna Nagar"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+91-9992396623",
      "contactType": "customer service",
      "availableLanguage": "English, Hindi"
    },
    "sameAs": [
      "https://www.facebook.com/bloodhero",
      "https://www.twitter.com/bloodhero",
      "https://www.instagram.com/bloodhero",
      "https://www.linkedin.com/company/bloodhero"
    ]
  };

  const finalStructuredData = structuredData || defaultStructuredData;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <meta name="keywords" content={finalKeywords} />
      <meta name="author" content="BloodHero" />
      <meta name="robots" content={noIndex ? "noindex, nofollow" : "index, follow"} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={finalUrl} />
      
      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={finalUrl} />
      <meta property="og:image" content={`${baseUrl}${finalImage}`} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="BloodHero" />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={`${baseUrl}${finalImage}`} />
      <meta name="twitter:site" content="@bloodhero" />
      <meta name="twitter:creator" content="@bloodhero" />
      
      {/* Additional SEO Meta Tags */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="theme-color" content="#dc2626" />
      <meta name="msapplication-TileColor" content="#dc2626" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content="BloodHero" />
      
      {/* Language and Region */}
      <meta httpEquiv="content-language" content="en" />
      <meta name="geo.region" content="IN-HR" />
      <meta name="geo.placename" content="Yamuna Nagar, Haryana, India" />
      
      {/* Business Information */}
      <meta name="business:contact_data:street_address" content="Yamuna Nagar, Haryana" />
      <meta name="business:contact_data:locality" content="Yamuna Nagar" />
      <meta name="business:contact_data:administrative_area" content="Haryana" />
      <meta name="business:contact_data:country_name" content="India" />
      <meta name="business:contact_data:phone_number" content="+91-9992396623" />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(finalStructuredData)}
      </script>
      
      {/* Preconnect to external domains for performance */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link rel="preconnect" href="https://maps.googleapis.com" />
      
      {/* Favicon and App Icons */}
      <link rel="icon" type="image/x-icon" href="/favicon.ico" />
      <link rel="apple-touch-icon" sizes="180x180" href="/logo192.png" />
      <link rel="icon" type="image/png" sizes="32x32" href="/logo192.png" />
      <link rel="icon" type="image/png" sizes="16x16" href="/logo192.png" />
      
      {/* Manifest for PWA */}
      <link rel="manifest" href="/manifest.json" />
    </Helmet>
  );
};

export default SEOHead;
